// DIRECTIVE-040 commit 1: pure-function ZIP -> zone fee resolver.
//
// Reads the static JSON built by scripts/delivery/build-fl-zip-to-
// zone.ts. No DB, no IO at runtime - this is a constant-time map
// lookup. The "live" fee resolver (with same-day-no-double + per-
// client override checks) lives in get-effective-fee.ts and uses
// this as one of its inputs.
//
// Out-of-FL ZIPs (or any ZIP not in the JSON) resolve to Zone 0 /
// pickup / $0. The signup flow uses this to flip the client into
// pickup mode + show the "Doral pickup" copy.

import data from "./fl-zip-to-zone.json";
import {
  FL_ZONES,
  PICKUP_ZONE,
  type ZoneNumber,
} from "./zones";

export type ZoneLookupResult = {
  fee: number;
  zone: ZoneNumber;
  county: string | null;
  // True when the ZIP wasn't found in the FL JSON -> pickup at Doral.
  isPickupFallback: boolean;
  // Normalized 5-digit ZIP the lookup actually used.
  zip: string;
};

const ZIP_RE = /^\d{5}$/;

function normalizeZip(input: string): string | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  // Accept "33431-1234" (ZIP+4) by chopping the +4.
  const five = trimmed.split("-")[0];
  if (!ZIP_RE.test(five)) return null;
  return five;
}

const TABLE: Record<string, { fee: number; zone: number; county: string }> =
  data as Record<string, { fee: number; zone: number; county: string }>;

export function getDeliveryFeeByZip(input: string): ZoneLookupResult | null {
  const zip = normalizeZip(input);
  if (!zip) return null;
  const hit = TABLE[zip];
  if (!hit) {
    return {
      fee: PICKUP_ZONE.fee,
      zone: PICKUP_ZONE.zone,
      county: null,
      isPickupFallback: true,
      zip,
    };
  }
  return {
    fee: hit.fee,
    zone: hit.zone as ZoneNumber,
    county: hit.county,
    isPickupFallback: false,
    zip,
  };
}

/**
 * Human-friendly label for a zone (used by cart + checkout line copy).
 * Returns "Doral pickup" for zone 0 / fallback.
 */
export function zoneLabel(zone: ZoneNumber): string {
  if (zone === 0) return PICKUP_ZONE.label;
  const z = FL_ZONES.find((s) => s.zone === zone);
  return z?.label ?? PICKUP_ZONE.label;
}

/** Total entries in the lookup table — used by lint tests. */
export function zipTableSize(): number {
  return Object.keys(TABLE).length;
}

// #442: the canonical "is this address out-of-Florida?" check, keyed off the
// SAME ZIP zone lookup the fee resolver + the signup delivery banner use - so
// the signup out-of-state QUESTION, the VALIDATOR, the SERVER route, the banner,
// and the actually-charged fee all agree. A ZIP that maps to a real FL delivery
// zone is Florida (not out-of-state); any other 5-digit ZIP (a non-FL ZIP, or a
// FL ZIP outside the delivery table - which the resolver already treats as an
// out-of-state $0 hand-off) is out-of-state. Before a valid 5-digit ZIP exists
// we DEFER (return false) so the question stays hidden - the banner is hidden
// then too. This replaces the #346 STATE-based check (a scraped ZIP was
// unreliable; the ZIP is now a dedicated, validated field, so it is safe again).
export function isOutOfStateByZip(zip: string | undefined | null): boolean {
  const z = (zip ?? "").trim();
  if (!/^\d{5}(-\d{4})?$/.test(z)) return false;
  const zone = getDeliveryFeeByZip(z);
  return !(zone && !zone.isPickupFallback);
}

/** Every county actually referenced in the JSON. Used by lint tests
 *  to assert no county defined in FL_ZONES is silently missing from
 *  the data + that no unexpected county slipped in (Census drift). */
export function countiesInTable(): string[] {
  const s = new Set<string>();
  for (const row of Object.values(TABLE)) s.add(row.county);
  return [...s].sort();
}
